import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useToast } from '../../../../../../components/toast';
import { getMembersList, addMembers, updateMember, removeMember } from '../../../services/members';

const DEFAULT_PERMISSION = 'Can edit';

const mapMemberRoleToPrivilege = (memberRole) => {
  const roleMap = {
    'ADMIN': 'Can edit',
    'APPROVER': 'Can view',
    'CONTRIBUTER': 'Can suggest',
  };
  return roleMap[memberRole] || 'Can view';
};

const mapPrivilegeToMemberRole = (privilegeName) => {
  const privilegeMap = {
    'Can edit': 'ADMIN',
    'Can view': 'APPROVER',
    'Can suggest': 'CONTRIBUTER',
  };
  return privilegeMap[privilegeName] || 'APPROVER';
};

const mapMemberRoleToApiRole = (memberRole) => {
  const roleMap = {
    'ADMIN': 'admin',
    'APPROVER': 'approver',
    'CONTRIBUTER': 'contributer',
  };
  return roleMap[memberRole] || 'approver';
};

export const useShareKnowledgeModal = (knowledgeBaseUuid, isOpen) => {
  const toast = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchRequestIdRef = useRef(0);

  // Fetch members
  const fetchMembers = useCallback(async (uuid) => {
    if (!uuid) return;

    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await getMembersList({
        knowledge_base_uuid: uuid,
        page: 1,
        page_size: 100,
      }, { persistAsBase: false });

      if (requestId !== fetchRequestIdRef.current) return;

      const body = Array.isArray(response.body) ? response.body : [];
      setMembers(body);
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;

      console.error('Error fetching members:', err);
      setError(err);
      setMembers([]);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Load members when modal opens
  useEffect(() => {
    if (isOpen && knowledgeBaseUuid) {
      fetchMembers(knowledgeBaseUuid);
    }
  }, [isOpen, knowledgeBaseUuid, fetchMembers]);

  // Transform members to format expected by CurrentUsersList
  const currentUsers = useMemo(() => {
    if (!members || !Array.isArray(members)) return [];
    
    return members.map((member) => {
      const memberUuid = member.memberUuid || member.member_uuid;
      const userUuid = member.userUuid || member.user_uuid || member.user_id || member.userId;
      const userFullName = member.userFullName || member.user_full_name || 'Unknown';
      const userEmail = member.userEmail || member.user_email || '';
      const memberRole = member.memberRole || member.member_role;

      return {
        grantee_uuid: userUuid,
        member_uuid: memberUuid,
        grantee_name: userFullName,
        grantee_email: userEmail,
        grantee_type: 'user',
        privileges: [{ privilege_name: mapMemberRoleToPrivilege(memberRole) }],
      };
    });
  }, [members]);

  const reloadCurrentMembers = useCallback(async () => {
    if (!knowledgeBaseUuid) return;
    await fetchMembers(knowledgeBaseUuid);
  }, [knowledgeBaseUuid, fetchMembers]);

  const handleUpdateCurrentPermission = useCallback(async (userId, userType, newPermission) => {
    if (!knowledgeBaseUuid) {
      toast.error({
        title: 'Error',
        message: 'Knowledge base UUID is required',
      });
      return;
    }

    const userToUpdate = currentUsers.find(user => user.grantee_uuid === userId);
    if (!userToUpdate || !userToUpdate.member_uuid) {
      toast.error({
        title: 'Error',
        message: 'Member information not found',
      });
      return;
    }

    const memberRole = mapPrivilegeToMemberRole(newPermission);
    const apiRole = mapMemberRoleToApiRole(memberRole);

    try {
      await updateMember({
        knowledge_base_uuid: knowledgeBaseUuid,
        member_uuid: userToUpdate.member_uuid,
        user_uuid: userId,
        new_role: apiRole,
      });

      await reloadCurrentMembers();

      toast.success({
        title: 'Permission updated',
        message: 'Member role updated successfully',
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      toast.error({
        title: 'Error',
        message: error.message || 'An error occurred while updating member role.',
      });
    }
  }, [knowledgeBaseUuid, currentUsers, reloadCurrentMembers, toast]);

  const handleRemoveCurrent = useCallback(async (userId, userType) => {
    if (!knowledgeBaseUuid) {
      toast.error({
        title: 'Error',
        message: 'Knowledge base UUID is required',
      });
      return;
    }

    const userToRemove = currentUsers.find(user => user.grantee_uuid === userId);
    if (!userToRemove || !userToRemove.member_uuid) {
      toast.error({
        title: 'Error',
        message: 'Member information not found',
      });
      return;
    }

    try {
      await removeMember({
        knowledge_base_uuid: knowledgeBaseUuid,
        member_uuid: userToRemove.member_uuid,
        user_uuid: userId,
      });

      await reloadCurrentMembers();

      toast.success({
        title: 'Member removed',
        message: 'Member removed successfully',
      });
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error({
        title: 'Error',
        message: error.message || 'An error occurred while removing member.',
      });
    }
  }, [knowledgeBaseUuid, currentUsers, reloadCurrentMembers, toast]);

  const handleAddMembers = useCallback(async (pendingUsers) => {
    if (!knowledgeBaseUuid) {
      throw new Error('Knowledge base UUID is required');
    }

    const membersToAdd = pendingUsers.map((user) => {
      const privilegeName = user.privileges[0]?.privilege_name || DEFAULT_PERMISSION;
      return {
        user_uuid: user.grantee_uuid,
        member_role: mapPrivilegeToMemberRole(privilegeName),
      };
    });

    await addMembers({
      knowledge_base_uuid: knowledgeBaseUuid,
      members: membersToAdd,
    });

    await reloadCurrentMembers();
  }, [knowledgeBaseUuid, reloadCurrentMembers]);

  return {
    currentUsers,
    loading,
    error,
    handleUpdateCurrentPermission,
    handleRemoveCurrent,
    handleAddMembers,
    reloadCurrentMembers,
  };
};

